import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from "@angular/core";
import {
  PasswordValidation,
  PasswordValidationCodes,
  PasswordValidationRegExp,
  VerifyPasswordState,
} from "../../../models/constants/auth.password.constants";
import { TranslateModule } from "@ngx-translate/core";
import { CommonModule } from "@angular/common";
import { CVerifyFormFields } from "./models/constants/verify-password.constants";
import { VerifyPasswordStateType } from "@pages/auth2/models/interfaces/auth.types";
import { IVerifyPassword } from "@pages/auth2/models/interfaces/auth.interfaces";

@Component({
  selector: "mdk-verify-password",
  templateUrl: "./verify-password.component.html",
  styleUrls: ["./styles/verify-password.component.mobile.scss"],
  standalone: true,
  imports: [TranslateModule, CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerifyPasswordComponent implements OnChanges {

  //#region [---- [INPUTS & OUTPUTS] ----]
  @Input() currentPassword: string;
  @Input() newPassword: string;
  @Input() confirmPassword: string;
  @Input() verifyList: IVerifyPassword[];
  @Output() isVerifyEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();
  //#endregion

  //#region [---- [PROPERTIES] ----]
  public readonly verify_state = VerifyPasswordState;
  //#endregion

  //#region [---- [LYFE CICLES] ----]
  ngOnChanges(changes: SimpleChanges): void {
    if (changes[CVerifyFormFields.newPassword]?.currentValue || changes[CVerifyFormFields.confirmPassword]?.currentValue) this.verifyPassword();
  }
  //#endregion

  //#region [---- [LOGIC] ----]
  private verifyPassword() {
    if (this.verifyList) {
      this.verifyList.forEach((validation) => {
        validation.state = VerifyPasswordState.NONE;
        if (validation.isEnabled) {
          switch (validation.code) {
            case PasswordValidationCodes.BE_DIFFERENT_TO_CURRENT:
              validation.state = this.validateBeDifferentToCurrent();
              break;
            case PasswordValidationCodes.MIN_LENGTH:
              validation.state = this.validateMinLength();
              break;
            case PasswordValidationCodes.NUMBER_UPPER_LOWER:
              validation.state = this.validateNumberUpperLower();
              break;
            case PasswordValidationCodes.SPECIAL_CARACTER:
              const validateSpecialCharacter = this.validateSpecialCharacter();
              const validateEmpty = this.validateEmpty();
              validation.state = (validateSpecialCharacter === VerifyPasswordState.VALID && validateEmpty === VerifyPasswordState.VALID)
                                 ? VerifyPasswordState.VALID : (validateSpecialCharacter === VerifyPasswordState.NONE && validateEmpty === VerifyPasswordState.NONE)
                                 ? VerifyPasswordState.NONE : VerifyPasswordState.INVALID;
              break;
            case PasswordValidationCodes.EXACT_MATCH:
              validation.state = this.validateExactMatch();
              break;
          }
        }
      });

      this.isVerifyEmitter.emit(
        this.verifyList.find(
          (validation) =>
          validation.state === VerifyPasswordState.INVALID
        )
        ? false : true
      );
    }
  }

  private validateBeDifferentToCurrent(): VerifyPasswordStateType {
    return this.newPassword ? (this.currentPassword !== this.newPassword ? VerifyPasswordState.VALID : VerifyPasswordState.INVALID) : VerifyPasswordState.NONE;
  }

  private validateMinLength(): VerifyPasswordStateType {
    return this.newPassword ? (this.newPassword?.length >= PasswordValidation.MIN_LENGTH ? VerifyPasswordState.VALID : VerifyPasswordState.INVALID) : VerifyPasswordState.NONE;
  }

  private validateNumberUpperLower(): VerifyPasswordStateType {
    const specialCharacterPattern = PasswordValidationRegExp.NUMBER_UPPER_LOWER;
    return this.newPassword ? (specialCharacterPattern.test(this.newPassword) ? VerifyPasswordState.VALID : VerifyPasswordState.INVALID) : VerifyPasswordState.NONE;
  }

  private validateSpecialCharacter(): VerifyPasswordStateType {
    const specialCharacterPattern = PasswordValidationRegExp.SPECIAL_CARACTER;
    return this.newPassword ? (specialCharacterPattern.test(this.newPassword) ? VerifyPasswordState.VALID : VerifyPasswordState.INVALID) : VerifyPasswordState.NONE;
  }

  private validateExactMatch(): VerifyPasswordStateType {
    return this.newPassword ? (this.newPassword === this.confirmPassword ? VerifyPasswordState.VALID : VerifyPasswordState.INVALID) : VerifyPasswordState.NONE;
  }

  private validateEmpty(): VerifyPasswordStateType {
    return this.newPassword ? (this.newPassword.length === this.newPassword.trim().length ? VerifyPasswordState.VALID : VerifyPasswordState.INVALID) : VerifyPasswordState.NONE;
  }
  //#endregion
}
